import React from 'react';
import PropTypes from 'prop-types';
import { Segment, Checkbox } from 'semantic-ui-react';
import { Trans, useTranslation } from 'react-i18next';
import { ToastContext } from '@weezevent/nacre';
import UserRoleOrganizationModel from '../../models/userRoleOrganizationModel';

/**
 * This component must be used in card ( nacre ), and is used to display the resaler checkbox and gives access rights at application resaler
 * on specific organizations.
 *
 * @param id : App id of resaler (app_41 for ticketswap)
 * @param title : i18nKey for trad
 * @param name : name of reseller ( to change boolean )
 * @param icon : Component containing the icon of ticketSwap or Reelax
 * @param isChecked : Resaler already have been rights
 * @param information : i18nKey for trad
 * @param link :I18nKey of link with the link to appear
 * @param warning : i18nKey for trad
 * @param onChange : func to change boolean ischecked
 * @param onError: func to remake the sync of rights
 * @param organizationId : Organizer ID
 */
export default function ResalerSegment({ id, name, title, icon, isChecked, information, link, warning = null, onChange, onError, organizationId }) {
    const toast = React.useContext(ToastContext);
    let [loading, setLoading] = React.useState(false);
    let { t } = useTranslation();

    /**
     * Call to create or delete UserRole
     */
    const _onChange = React.useCallback(
        (e, data) => {
            let rights = {
                context: ['*', '*', String(organizationId)],
                user: {
                    username: id
                }
            };
            setLoading(true);
            let promise = null;
            if (data.checked) {
                let dataObjects = new UserRoleOrganizationModel(rights);
                promise = dataObjects
                    .save({ role: 'ticketswap', organizationId: organizationId })
                    .then(response => {
                        onChange(name, data.checked);
                        toast.info(t('ticket.resales.resalers.adding_reseller_success'));
                    })
                    .catch(e => {
                        onError();
                        toast.error(t('ticket.resales.resalers.adding_reseller_error'));
                    });
            } else {
                // delete
                let dataObjects = new UserRoleOrganizationModel(rights);
                promise = dataObjects
                    .delete({ role: 'ticketswap', organizationId: organizationId, data: { ...rights } })
                    .then(response => {
                        onChange(name, data.checked);
                        toast.info(t('ticket.resales.resalers.deleting_reseller_success'));
                    })
                    .catch(e => {
                        onError();
                        toast.error(t('ticket.resales.resalers.deleting_reseller_error'));
                    });
            }

            promise.finally(() => setLoading(false));
        },
        [id, onChange, onError, setLoading]
    );

    return (
        <Segment>
            <div className={'resalers_segment'}>
                <div className={'resalers_name'}>
                    {icon}
                    <Trans i18nKey={title}>Resaler</Trans>
                </div>
                <Checkbox toggle checked={isChecked} onChange={_onChange} disabled={loading} />
            </div>
            <div className={'resalers_information'}>
                <p>
                    <Trans i18nKey={information}>
                        <LinkTo link={link} />
                    </Trans>
                </p>
                {warning ? (
                    <p>
                        <Trans i18nKey={warning}>Information about TicketSwap</Trans>
                    </p>
                ) : null}
            </div>
        </Segment>
    );
}

ResalerSegment.propTypes = {
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    icon: PropTypes.object.isRequired,
    isChecked: PropTypes.bool.isRequired,
    information: PropTypes.string.isRequired,
    link: PropTypes.string.isRequired,
    warning: PropTypes.string,
    onChange: PropTypes.func.isRequired,
    onError: PropTypes.func.isRequired,
    organizationId: PropTypes.string.isRequired
};

function LinkTo({ link }) {
    return (
        <a href={link} target="_blank" rel="noopener noreferrer">
            {link}
        </a>
    );
}

LinkTo.propTypes = {
    link: PropTypes.string.isRequired
};
